import { useCallback, useEffect, useState } from "react";
import { MessageCircle, Send, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, shortDate } from "../lib/api";
import UpgradeGate from "../components/UpgradeGate";

export default function Automations({ user, onUpgraded }) {
  const [settings, setSettings] = useState(null);
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);

  const isPro = user.plan === "pro";

  const load = useCallback(() => {
    if (!isPro) return;
    Promise.all([
      api.get("/settings/whatsapp").then((r) => setSettings(r.data && Object.keys(r.data).length ? r.data : { provider: "twilio", account_sid: "", auth_token: "", from_number: "", meta_phone_id: "", meta_access_token: "", reminder_template: "Hi {customer}, this is a friendly reminder that invoice {number} of {amount} is due on {due_date}. Thank you!", reminders_enabled: false })),
      api.get("/whatsapp/messages").then((r) => setMessages(r.data)),
    ]).catch(() => toast.error("Could not load automations"));
  }, [isPro]);

  useEffect(() => { load(); }, [load]);

  if (!isPro) {
    return (
      <UpgradeGate
        title="Automations are included in the Pro plan"
        description="Let FlowSphere handle the boring parts — WhatsApp reminders and follow-ups for overdue invoices, plus queued sends you can review at a glance."
        benefits={[
          "Automated overdue-invoice reminders on WhatsApp",
          "Customisable message template with customer name, invoice number, amount and due date",
          "Bring your own Twilio or WhatsApp Business Cloud API credentials",
          "Full queue with status: Queued, Sent, Awaiting credentials",
        ]}
        onUpgraded={onUpgraded}
      />
    );
  }

  if (!settings) return <div className="skeleton-grid"><div /><div /><div /></div>;

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/whatsapp", settings);
      toast.success("WhatsApp settings saved");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save settings");
    } finally {
      setBusy(false);
    }
  };

  const queueReminders = async () => {
    try {
      const response = await api.post("/whatsapp/reminders/queue");
      if (response.data.queued === 0) toast.info("No overdue invoices to remind about right now");
      else toast.success(`${response.data.queued} reminder(s) queued`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not queue reminders");
    }
  };

  const sendMessage = async (msgId) => {
    try {
      await api.post(`/whatsapp/messages/${msgId}/send`);
      toast.success("Message sent via your WhatsApp API");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not send this message");
      load();
    }
  };

  const provider = settings.provider || "twilio";

  return (
    <div className="automations-grid">
      <section className="panel" data-testid="whatsapp-settings-panel">
        <div className="panel-heading">
          <div><h3>WhatsApp API credentials</h3><p>Paste your own API keys — messages are sent from your account, not ours.</p></div>
          <span className="status paid"><ShieldCheck size={12} style={{ marginRight: 4 }} /> Bring your own API</span>
        </div>
        <div className="settings-form">
          <label className="modal-field">
            Provider
            <select data-testid="whatsapp-provider-input" value={provider} onChange={(e) => setSettings({ ...settings, provider: e.target.value })}>
              <option value="twilio">Twilio WhatsApp</option>
              <option value="meta">WhatsApp Business Cloud API (Meta)</option>
            </select>
          </label>
          {provider === "twilio" ? (
            <>
              <label className="modal-field">Account SID<input data-testid="whatsapp-sid-input" value={settings.account_sid || ""} onChange={(e) => setSettings({ ...settings, account_sid: e.target.value })} placeholder="ACxxxxxxxxxxxxxxxx" /></label>
              <label className="modal-field">Auth Token<input data-testid="whatsapp-token-input" type="password" value={settings.auth_token || ""} onChange={(e) => setSettings({ ...settings, auth_token: e.target.value })} placeholder="Your Twilio auth token" /></label>
              <label className="modal-field">From number<input data-testid="whatsapp-from-input" value={settings.from_number || ""} onChange={(e) => setSettings({ ...settings, from_number: e.target.value })} placeholder="whatsapp:+14155238886" /></label>
            </>
          ) : (
            <>
              <label className="modal-field">Phone number ID<input data-testid="whatsapp-meta-phone-input" value={settings.meta_phone_id || ""} onChange={(e) => setSettings({ ...settings, meta_phone_id: e.target.value })} placeholder="Your Meta phone number ID" /></label>
              <label className="modal-field">Access token<input data-testid="whatsapp-meta-token-input" type="password" value={settings.meta_access_token || ""} onChange={(e) => setSettings({ ...settings, meta_access_token: e.target.value })} placeholder="Your Meta access token" /></label>
            </>
          )}
          <label className="modal-field wide-field">
            Reminder template
            <textarea data-testid="whatsapp-template-input" rows="3" value={settings.reminder_template || ""} onChange={(e) => setSettings({ ...settings, reminder_template: e.target.value })} placeholder="Hi {customer}, invoice {number} of {amount} is due on {due_date}." />
          </label>
          <div className="settings-footer">
            <label className="switch">
              <input type="checkbox" data-testid="whatsapp-enabled-input" checked={!!settings.reminders_enabled} onChange={(e) => setSettings({ ...settings, reminders_enabled: e.target.checked })} />
              <span>Enable overdue reminders</span>
            </label>
            <button className="primary-button" disabled={busy} onClick={save} data-testid="whatsapp-save-button">Save credentials</button>
          </div>
        </div>
      </section>

      <section className="panel" data-testid="whatsapp-queue-panel">
        <div className="panel-heading">
          <div><h3>Reminder queue</h3><p>Overdue invoices are picked up automatically. Review and send when ready.</p></div>
          <button className="secondary-button" data-testid="whatsapp-queue-button" onClick={queueReminders}><RefreshCw size={14} /> Queue overdue</button>
        </div>
        <div className="whatsapp-list">
          {messages.length === 0 && (
            <div className="mini-empty">No queued reminders. Click "Queue overdue" to generate them.</div>
          )}
          {messages.map((message) => (
            <div className="whatsapp-row" key={message.id} data-testid={`whatsapp-message-${message.id}`}>
              <div className="whatsapp-avatar"><MessageCircle size={16} /></div>
              <div className="whatsapp-body">
                <strong>{message.customer_name} · {message.invoice_number}</strong>
                <small>{message.phone}</small>
                <p>{message.message}</p>
                <span className={`status ${message.status.toLowerCase().replace(/\s+/g, "-")}`}>{message.status}</span>
                {message.status === "Awaiting credentials" && message.last_error && <em className="whatsapp-error">{message.last_error}</em>}
                <em className="whatsapp-time">Queued {shortDate(message.created_at)}</em>
              </div>
              {message.status !== "Sent" && (
                <button className="link-button" data-testid={`whatsapp-send-${message.id}`} onClick={() => sendMessage(message.id)}><Send size={12} /> Send</button>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
